import { Fragment } from "react";
import {
  TableCell,
  Avatar,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Box,
  Stack,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatIcon from "@mui/icons-material/Chat";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ClientTimeline from "./ClientTimeline";
import ClienteComercialResumen from "./expansion/ClienteComercialResumen";
import StageChip from "./ui/StageChip";
import { canalLabel } from "./pipelineUtils";
import { WHATSAPP_GREEN } from "./theme/brand";



export default function LeadRow({
  lead,
  etapaNombre,
  contactos,
  config,
  historialEtapas,
  historialEdicionesLead,
  historialAsignaciones,
  isExpanded,
  onToggle,
  onWA,
  onContacto,
  onEdit,
  onDelete,
  diasEntreSeguimiento,
  isMobile,
  store,
  onNuevaOportunidad,
  onEditOportunidad,
  onSeguimientoOportunidad,
}) {
  const archiveAction = store?.isOwner && (
    <Tooltip title={lead.archivado ? "Restaurar cliente" : "Archivar cliente"}>
      <IconButton size="small" onClick={() => store.archivarLead(lead.id, !lead.archivado)} aria-label={lead.archivado ? "Restaurar cliente" : "Archivar cliente"}>
        {lead.archivado ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
  const vendedor = config.vendedores?.find(v => v.id === lead.vendedorId);
  const sellerAvatar = vendedor && <Tooltip title={`Asignado a ${vendedor.nombre}`}><Avatar sx={{ bgcolor: vendedor.color || '#555', color: '#fff', width: 24, height: 24, fontSize: 12 }}>{vendedor.nombre?.[0]?.toUpperCase()}</Avatar></Tooltip>;
  const diasSinContacto = lead.ultimoContacto
    ? Math.floor((Date.now() - new Date(lead.ultimoContacto).getTime()) / 86400000)
    : null;

  const colorUltimoContacto = (() => {
    if (diasSinContacto === null) return "error.main";
    if (diasSinContacto >= 3 && lead.etapa !== "inscrito" && lead.etapa !== "perdido") return "error.main";
    return "text.secondary";
  })();

  const textoUltimoContacto = diasSinContacto === null
    ? "Sin contactar"
    : diasSinContacto === 0 ? "Hoy"
    : diasSinContacto === 1 ? "Hace 1 día"
    : `Hace ${diasSinContacto} días`;

  const fontWeightContacto = (diasSinContacto === null || (diasSinContacto >= 3 && lead.etapa !== "inscrito")) ? 700 : 400;

  // Próxima visita sugerida
  const proximaVisita = (() => {
    if (!lead.ultimoContacto || !diasEntreSeguimiento) return null;
    if (lead.etapa === "inscrito" || lead.etapa === "perdido") return null;
    const fecha = new Date(new Date(lead.ultimoContacto).getTime() + diasEntreSeguimiento * 86400000);
    const hoy = new Date(); hoy.setHours(0,0,0,0);
    const diff = Math.ceil((fecha.getTime() - hoy.getTime()) / 86400000);
    if (diff < 0) return { texto: `vencida hace ${Math.abs(diff)} d`, color: "error" };
    if (diff === 0) return { texto: "hoy", color: "warning" };
    if (diff <= 2) return { texto: `en ${diff} d`, color: "warning" };
    return { texto: `en ${diff} d`, color: "default" };
  })();

  const historialContent = (
    <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: "background.default" }}>
      {store && !lead.archivado && (
        <ClienteComercialResumen
          store={store}
          leadId={lead.id}
          compact
          onNuevaOportunidad={onNuevaOportunidad}
          onEditOportunidad={onEditOportunidad}
          onSeguimientoOportunidad={onSeguimientoOportunidad}
        />
      )}
      {lead.archivado && <Box mb={2}>
        <Typography variant="subtitle2" fontWeight={700}>Historial de compras</Typography>
        {(store?.ventas || []).filter(v => v.leadId === lead.id).map(v => <Typography key={v.id} variant="body2">{v.fecha?.slice(0, 10)} · {v.productoNombre || "Venta"} · {config.moneda} {v.total}{v.anulada ? " (anulada)" : ""}</Typography>)}
      </Box>}
      {lead.observaciones && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          <strong>Observaciones:</strong> {lead.observaciones}
        </Typography>
      )}
      <ClientTimeline
        key={lead.id}
        leadId={lead.id}
        config={config}
        contactos={contactos}
        tareas={store?.tareasFollowup || []}
        etapas={historialEtapas || []}
        ediciones={historialEdicionesLead || []}
        asignaciones={historialAsignaciones || []}
        oportunidades={store?.oportunidadesExpansion || []}
      />
    </Box>
  );

  if (lead.archivado) {
    const actions = <Stack direction="row" alignItems="center" spacing={1}>{archiveAction}<IconButton size="small" onClick={onToggle} aria-label={isExpanded ? "Ocultar detalle" : "Ver detalle"}>{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton></Stack>;
    if (isMobile) return <Card variant="outlined"><CardContent><Stack direction="row" justifyContent="space-between"><Typography fontWeight={600}>{lead.nombre}</Typography>{actions}</Stack><Typography variant="caption">Archivado</Typography><Collapse in={isExpanded}>{isExpanded && historialContent}</Collapse></CardContent></Card>;
    return <Fragment><TableRow><TableCell colSpan={6}><Typography fontWeight={600}>{lead.nombre}</Typography><Typography variant="caption">Archivado</Typography></TableCell><TableCell>{actions}</TableCell></TableRow><TableRow><TableCell colSpan={7} sx={{ p: 0, border: 0 }}><Collapse in={isExpanded}>{isExpanded && historialContent}</Collapse></TableCell></TableRow></Fragment>;
  }

  if (isMobile) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <CardContent sx={{ pb: "8px !important", pt: 1.5, px: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {sellerAvatar}
              <Typography variant="subtitle2" fontWeight={700} noWrap>{lead.nombre}</Typography>
              <Typography variant="caption" color="text.secondary">
                {lead.telefono || "—"} {lead.edad ? `· ${lead.edad} años` : ""}
              </Typography>
            </Box>
            <StageChip etapa={lead.etapa} label={etapaNombre} sx={{ ml: 1, flexShrink: 0 }} />
          </Stack>

          {lead.cursoInteres && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              Interés: {lead.cursoInteres}
            </Typography>
          )}

          <Stack direction="row" spacing={0.5} mt={0.75} flexWrap="wrap" useFlexGap>
            <Chip label={canalLabel(lead.canal)} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
            {lead.zona && <Chip label={lead.zona} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" mt={1}>
            <Typography variant="caption" sx={{ color: colorUltimoContacto, fontWeight: fontWeightContacto }}>
              {textoUltimoContacto}
            </Typography>
            {proximaVisita && (
              <Chip label={`Revisita ${proximaVisita.texto}`} size="small" color={proximaVisita.color} variant="outlined" sx={{ fontSize: 10, height: 18 }} />
            )}
          </Stack>
        </CardContent>

        <Divider />
        <Stack direction="row" justifyContent="space-between" alignItems="center" px={1} py={0.25}>
          <Stack direction="row" spacing={0}>
            {lead.telefono && (
              <IconButton size="small" sx={{ color: WHATSAPP_GREEN }} onClick={onWA} aria-label="Enviar WhatsApp"><WhatsAppIcon fontSize="small" /></IconButton>
            )}
            <IconButton size="small" color="primary" onClick={onContacto} aria-label="Registrar contacto"><ChatIcon fontSize="small" /></IconButton>
            {archiveAction}
            <IconButton size="small" onClick={onEdit} aria-label="Editar lead"><EditIcon fontSize="small" /></IconButton>
            <IconButton size="small" color="error" onClick={onDelete} aria-label="Eliminar lead"><DeleteIcon fontSize="small" /></IconButton>
          </Stack>
          <IconButton size="small" onClick={onToggle} aria-label={isExpanded ? "Ocultar detalle" : "Ver detalle"}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>

        <Collapse in={isExpanded} timeout="auto">
          {isExpanded && historialContent}
        </Collapse>
      </Card>
    );
  }

  return (
    <Fragment>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={onToggle} aria-label={isExpanded ? "Ocultar detalle" : "Ver detalle"}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">{sellerAvatar}<Typography variant="body2" fontWeight={600}>{lead.nombre}</Typography></Stack>
          <Typography variant="caption" color="text.secondary">
            {lead.telefono || "—"} {lead.edad ? `· ${lead.edad} años` : ""}
          </Typography>
        </TableCell>
        <TableCell>{lead.cursoInteres || "—"}</TableCell>
        <TableCell><Chip label={canalLabel(lead.canal)} size="small" variant="outlined" />{lead.zona && <Chip label={lead.zona} size="small" variant="outlined" sx={{ ml: 0.5, fontSize: 10 }} />}</TableCell>
        <TableCell><StageChip etapa={lead.etapa} label={etapaNombre} /></TableCell>
        <TableCell>
          <Typography variant="caption" sx={{ color: colorUltimoContacto, fontWeight: fontWeightContacto }}>
            {textoUltimoContacto}
          </Typography>
          {proximaVisita && (
            <Chip label={`Revisita ${proximaVisita.texto}`} size="small" color={proximaVisita.color} variant="outlined" sx={{ ml: 0.5, fontSize: 10, height: 18 }} />
          )}
        </TableCell>
        <TableCell>
          {lead.telefono && (
            <Tooltip title="Enviar WhatsApp">
              <IconButton size="small" sx={{ color: WHATSAPP_GREEN }} onClick={onWA} data-onboarding-id="onb-wa-btn" aria-label="Enviar WhatsApp"><WhatsAppIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
          <Tooltip title="Registrar contacto">
            <IconButton size="small" color="primary" onClick={onContacto} aria-label="Registrar contacto"><ChatIcon fontSize="small" /></IconButton>
          </Tooltip>
          {archiveAction}
          <Tooltip title="Editar lead">
            <IconButton size="small" onClick={onEdit} aria-label="Editar lead"><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Eliminar lead">
            <IconButton size="small" color="error" onClick={onDelete} aria-label="Eliminar lead"><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
          <Collapse in={isExpanded} timeout="auto">
            {isExpanded && historialContent}
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}
